import {useCallback, useEffect, useRef, useState} from 'react'
import {useRemoveBgHistory} from './useRemoveBgHistory'
import {useRemoveBgZoomPan} from './useRemoveBgZoomPan'
import {useRemoveBgCanvas} from './useRemoveBgCanvas'
import {useRemoveBgBrush} from './useRemoveBgBrush'

/**
 * @typedef {Object} RemoveBgEditorContract
 *
 * @property {React.RefObject<HTMLCanvasElement>} canvasRef
 * @property {React.RefObject<HTMLDivElement>} containerRef
 *
 * @property {boolean} ready          - true after canvas + mask are initialized
 * @property {'brush'|'hand'} tool    - active tool
 * @property {'erase'|'restore'} mode - brush paint mode
 * @property {number} brushSize       - brush diameter in image pixels
 *
 * @property {(tool: 'brush'|'hand') => void} setTool
 * @property {(mode: 'erase'|'restore') => void} setMode
 * @property {(size: number) => void} setBrushSize
 * @property {() => void} undo
 * @property {() => void} redo
 * @property {() => void} revertAll        - reset mask to initial (first) history entry
 * @property {() => Promise<Blob>} exportBlob - export composited PNG at original resolution
 * @property {(src: string, blob: Blob) => Promise<void>} applyMlResult - replace mask with ML output
 * @property {(src: string) => Promise<void>} initWithOriginal - initialize with original image
 * @property {() => void} resetEditor      - clear all state (call when modal closes)
 *
 * @property {boolean} canUndo
 * @property {boolean} canRedo
 * @property {number} zoom             - current zoom factor (1 = 100%)
 * @property {number} zoomPercent       - zoom as integer percentage
 *
 * @property {() => void} zoomIn
 * @property {() => void} zoomOut
 * @property {(e: WheelEvent) => void} handleWheel
 * @property {(e: PointerEvent) => void} handleCanvasPointerDown
 * @property {(e: PointerEvent) => void} handleCanvasPointerMove
 * @property {(e: PointerEvent) => void} handleCanvasPointerUp
 */

/**
 * Orchestrator hook for the remove-bg editor.
 * Combines history, zoom/pan, canvas rendering, and brush tools.
 *
 * @param {{ imageSrc: string|null }} params
 * @returns {RemoveBgEditorContract}
 */
export function useRemoveBgEditor({imageSrc}) {
    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const [tool, setTool] = useState('brush')
    const [mode, setMode] = useState('erase')
    const [brushSize, setBrushSize] = useState(56)
    const [ready, setReady] = useState(false)

    // History
    const history = useRemoveBgHistory()

    // Zoom/Pan
    const zoomPan = useRemoveBgZoomPan({containerRef})

    // Canvas rendering
    const canvas = useRemoveBgCanvas({
        canvasRef,
        zoom: zoomPan.zoom,
        pan: zoomPan.pan,
    })

    // Save mask snapshot after each brush stroke
    const handleStrokeEnd = useCallback(() => {
        const imageData = canvas.getMaskImageData()
        if (imageData) {
            history.pushState(imageData)
        }
    }, [canvas, history])

    // Brush
    const brush = useRemoveBgBrush({
        getMaskContext: canvas.getMaskContext,
        clientToImage: canvas.clientToImage,
        onStrokeEnd: handleStrokeEnd,
        scheduleRender: canvas.render,
    })

    /**
     * Initialize editor with original image (full opaque mask = keep everything).
     * Called immediately when modal opens — no ML processing needed.
     */
    const initWithOriginal = useCallback(
        async (src) => {
            if (!src) return
            const initialMask = await canvas.initFullOpaqueMask(src)
            history.reset()
            history.pushState(initialMask)
            const origImg = canvas.originalImageRef.current
            if (origImg) {
                zoomPan.fitToView(origImg.naturalWidth, origImg.naturalHeight)
            }
            setReady(true)
            canvas.render()
        },
        [canvas, history, zoomPan],
    )

    /**
     * Apply ML-generated mask (when user clicks "Auto remove").
     * Replaces current mask with the one extracted from the ML result.
     */
    const applyMlResult = useCallback(
        async (src, blob) => {
            if (!src || !blob) return
            const mlMask = await canvas.initMaskFromBlob(src, blob)
            history.reset()
            history.pushState(mlMask)
            const origImg = canvas.originalImageRef.current
            if (origImg) {
                zoomPan.fitToView(origImg.naturalWidth, origImg.naturalHeight)
            }
            canvas.render()
        },
        [canvas, history, zoomPan],
    )

    // Always keep a ref to the latest initWithOriginal so the effect below never
    // re-fires just because canvas/history object references changed on re-render.
    const initWithOriginalRef = useRef(initWithOriginal)
    initWithOriginalRef.current = initWithOriginal

    // Initialize when imageSrc is available (immediate, no ML needed).
    // Only re-run when imageSrc itself changes, not on every render.
    useEffect(() => {
        if (imageSrc) {
            initWithOriginalRef.current(imageSrc)
        }
    }, [imageSrc])

    /**
     * Undo last brush stroke.
     */
    const undo = useCallback(() => {
        const imageData = history.undo()
        if (imageData) {
            canvas.restoreMask(imageData)
        }
    }, [history, canvas])

    /**
     * Redo last undone brush stroke.
     */
    const redo = useCallback(() => {
        const imageData = history.redo()
        if (imageData) {
            canvas.restoreMask(imageData)
        }
    }, [history, canvas])

    /**
     * Revert to the initial ML-generated mask.
     */
    const revertAll = useCallback(() => {
        const imageData = history.revertToInitial()
        if (imageData) {
            canvas.restoreMask(imageData)
        }
    }, [history, canvas])

    /**
     * Export current canvas state as a Blob.
     */
    const exportBlob = useCallback(() => {
        return canvas.exportBlob()
    }, [canvas])

    /**
     * Canvas pointer handlers — delegates to brush or pan based on active tool.
     * Uses pointer capture so brush strokes are not lost when pointer leaves the canvas.
     */
    const handleCanvasPointerDown = useCallback(
        (e) => {
            if (tool === 'hand') {
                zoomPan.startPan(e)
            } else {
                e.target.setPointerCapture(e.pointerId)
                brush.startStroke(e, brushSize, mode)
            }
        },
        [tool, zoomPan, brush, brushSize, mode],
    )

    const handleCanvasPointerMove = useCallback(
        (e) => {
            if (tool === 'hand') {
                zoomPan.movePan(e)
            } else {
                brush.moveStroke(e, brushSize, mode)
            }
        },
        [tool, zoomPan, brush, brushSize, mode],
    )

    const handleCanvasPointerUp = useCallback(
        (e) => {
            if (tool === 'hand') {
                zoomPan.endPan()
            } else {
                if (e.target.hasPointerCapture(e.pointerId)) {
                    e.target.releasePointerCapture(e.pointerId)
                }
                brush.endStroke()
            }
        },
        [tool, zoomPan, brush],
    )

    /**
     * Reset editor state (when modal closes).
     */
    const resetEditor = useCallback(() => {
        history.reset()
        zoomPan.resetView()
        setTool('brush')
        setMode('erase')
        setBrushSize(56)
        setReady(false)
    }, [history, zoomPan])

    return {
        // Refs
        canvasRef,
        containerRef,

        // State
        ready,
        tool,
        mode,
        brushSize,

        // Actions
        setTool,
        setMode,
        setBrushSize,
        undo,
        redo,
        revertAll,
        exportBlob,
        applyMlResult,
        initWithOriginal,
        resetEditor,

        // History
        canUndo: history.canUndo,
        canRedo: history.canRedo,

        // Zoom/Pan
        zoom: zoomPan.zoom,
        zoomPercent: zoomPan.zoomPercent,
        zoomIn: zoomPan.zoomIn,
        zoomOut: zoomPan.zoomOut,
        handleWheel: zoomPan.handleWheel,

        // Canvas pointer handlers
        handleCanvasPointerDown,
        handleCanvasPointerMove,
        handleCanvasPointerUp,
    }
}
